import { HEADER_HEIGHT } from 'components/Layout/Master';
import { TableLoader } from 'components/TableLoader';
import type { ThreeForms } from 'components/TotalCount';
import { TotalCount } from 'components/TotalCount';
import { useAccess } from 'hooks/useAccess';
import { Button, Grid, GridItem, HeaderSearchBar, IconClose, Table, cnMixSpace } from 'uikit';

import type { TableProps } from '@consta/uikit/Table';
import type { CSSProperties } from 'react';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { CreateCatalog } from '../CreateCatalog';

import { getCatalogListTagLabel } from './helpers';
import { useCatalogListColumns, useCatalogListData, useCatalogListFilters } from './hooks';

import Styles from './catalogList.module.css';

const COUNT_FORMS = ['справочник', 'справочника', 'справочников'] as ThreeForms;
const TITLE_HEIGHT = 190;

export const CatalogList = () => {
    const navigate = useNavigate();

    const { catalogs: catalogsRules } = useAccess();

    const {
        tableData,
        fetchingStatus,
        searchString,
        handleClearSearch,
        handleChangeSearchString,
        totalRowCount,
        handleOnFiltersUpdated,
        handleOnSortUpdated,
        getCatalogList,
    } = useCatalogListData();

    const tableColumns = useCatalogListColumns();
    const tableFilters = useCatalogListFilters();

    const handleRowClick = useCallback<NonNullable<TableProps<(typeof tableData)[number]>['onRowClick']>>(
        ({ id }) => {
            const currentCatalog = tableData.find((catalog) => catalog.id === id);

            if (currentCatalog) {
                const {
                    id: catalogId,
                    type: { uniqueId },
                } = currentCatalog;

                navigate(`/catalogs/${uniqueId}/${catalogId}`);
            }
        },
        [navigate, tableData],
    );

    return (
        <div id="catalogList" style={{ '--others-height': `${HEADER_HEIGHT + TITLE_HEIGHT}px` } as CSSProperties}>
            <div className={Styles.header} style={{ height: TITLE_HEIGHT }}>
                <h2>Реестр справочников</h2>

                <Grid cols="2" colGap="xl" rowGap="l" className={cnMixSpace({ mB: 'm' })}>
                    <GridItem className={Styles.searchBarContainer}>
                        <HeaderSearchBar
                            placeholder="Найти справочник"
                            label="поиск"
                            value={searchString}
                            onChange={handleChangeSearchString}
                            className={Styles.searchBar}
                        />

                        {searchString && (
                            <Button
                                label="Очистить"
                                view="ghost"
                                iconLeft={IconClose}
                                form="round"
                                size="xs"
                                onlyIcon
                                onClick={handleClearSearch}
                                className={Styles.searchBarClearButton}
                            />
                        )}
                    </GridItem>

                    <GridItem className={Styles.createBtn}>
                        <CreateCatalog updateCatalogList={getCatalogList} catalogsRules={catalogsRules} />
                    </GridItem>

                    <GridItem>
                        <TotalCount totalCount={totalRowCount} forms={COUNT_FORMS} />
                    </GridItem>
                </Grid>
            </div>

            <Table
                columns={tableColumns}
                rows={tableData}
                filters={tableFilters as any}
                onFiltersUpdated={handleOnFiltersUpdated}
                getTagLabel={getCatalogListTagLabel}
                onSortBy={handleOnSortUpdated}
                onRowClick={handleRowClick}
                emptyRowsPlaceholder={<TableLoader requestStatus={fetchingStatus} />}
                // isResizable
                stickyHeader
                borderBetweenRows
                className={Styles.table}
            />
        </div>
    );
};

CatalogList.displayName = 'CatalogList';
